import { Log } from '../logger.js';
import { managedEffect, cleanupEffects } from '../signals.js';
import { ErrorBoundary } from '../errorBoundary.js';

/**
 * Base class for all web components with enhanced lifecycle management.
 * Provides automatic cleanup, error handling, and resource management.
 * 
 * @example
 * class MyComponent extends BaseComponent {
 *   connectedCallback() {
 *     super.connectedCallback();
 *     this.setupMyFeatures();
 *   }
 * }
 */
export class BaseComponent extends HTMLElement {
  constructor(componentName = 'BaseComponent') {
    super();
    
    // Initialize logging with component name
    this._log = Log(componentName);
    this._componentName = componentName;
    
    // Lifecycle management
    this._cleanupFunctions = [];
    this._eventListeners = [];
    this._intervals = [];
    this._timeouts = [];
    this._abortControllers = [];
    
    // State tracking
    this._isConnected = false;
    this._isInitialized = false;
    
    this._log.blue(`${componentName} constructor called`);
  }
  
  /**
   * Standard connectedCallback with enhanced lifecycle management
   */
  connectedCallback() {
    if (this._isConnected) {
      this._log.yellow('Component already connected, skipping');
      return;
    }
    
    this._isConnected = true;
    this._log.green(`${this._componentName} connected to DOM`);
    
    try {
      this._initialize();
      this._isInitialized = true;
    } catch (error) {
      this._log.red('Error during component initialization:', error);
      ErrorBoundary.reportError(error, this._componentName);
      this._renderErrorState();
    }
  }
  
  /**
   * Standard disconnectedCallback with comprehensive cleanup
   */
  disconnectedCallback() {
    if (!this._isConnected) {
      this._log.yellow('Component already disconnected, skipping');
      return;
    }
    
    this._log.blue(`${this._componentName} disconnecting from DOM`);
    
    try {
      this._cleanup();
      this._isConnected = false;
      this._isInitialized = false;
      this._log.green(`${this._componentName} successfully disconnected`);
    } catch (error) {
      this._log.red('Error during component cleanup:', error);
      ErrorBoundary.reportError(error, this._componentName);
    }
  }
  
  /**
   * Initialize the component - override in subclasses
   */
  _initialize() {
    // Override in subclasses
    this._log.blue('Base initialization complete');
  }
  
  /**
   * Comprehensive cleanup of all resources
   */
  _cleanup() {
    const startTime = performance.now();
    
    // Clean up effects and signals
    cleanupEffects(this._cleanupFunctions);
    
    // Remove event listeners
    this._removeAllEventListeners();
    
    // Clear intervals and timeouts
    this._clearAllTimers();
    
    // Abort any ongoing requests
    this._abortAllRequests();
    
    const endTime = performance.now();
    this._log.green(`Cleanup completed in ${(endTime - startTime).toFixed(2)}ms`);
  }
  
  /**
   * Add a managed effect that will be automatically cleaned up
   */
  addManagedEffect(fn) {
    if (!this._isConnected) {
      this._log.yellow('Cannot add effect to disconnected component');
      return () => {}; // Return no-op cleanup
    }
    
    return managedEffect(fn, this._cleanupFunctions);
  }
  
  /**
   * Add a managed event listener that will be automatically removed
   */
  addManagedEventListener(target, event, handler, options = {}) {
    if (!this._isConnected) {
      this._log.yellow('Cannot add event listener to disconnected component');
      return;
    }
    
    const listener = {
      target,
      event,
      handler,
      options,
      cleanup: () => {
        target.removeEventListener(event, handler, options);
        this._log.blue(`Removed event listener: ${event}`);
      }
    };
    
    target.addEventListener(event, handler, options);
    this._eventListeners.push(listener);
    
    this._log.blue(`Added managed event listener: ${event}`);
  }
  
  /**
   * Add a managed interval that will be automatically cleared
   */
  addManagedInterval(callback, delay) {
    if (!this._isConnected) {
      this._log.yellow('Cannot add interval to disconnected component');
      return 0;
    }
    
    const intervalId = setInterval(callback, delay);
    this._intervals.push(intervalId);
    
    this._log.blue(`Added managed interval: ${intervalId} (${delay}ms)`);
    return intervalId;
  }
  
  /**
   * Add a managed timeout that will be automatically cleared
   */
  addManagedTimeout(callback, delay) {
    if (!this._isConnected) {
      this._log.yellow('Cannot add timeout to disconnected component');
      return 0;
    }
    
    const timeoutId = setTimeout(() => {
      // Remove from tracking when it executes
      const index = this._timeouts.indexOf(timeoutId);
      if (index > -1) {
        this._timeouts.splice(index, 1);
      }
      callback();
    }, delay);
    
    this._timeouts.push(timeoutId);
    
    this._log.blue(`Added managed timeout: ${timeoutId} (${delay}ms)`);
    return timeoutId;
  }
  
  /**
   * Add a managed AbortController for fetch requests
   */
  createManagedAbortController() {
    if (!this._isConnected) {
      this._log.yellow('Cannot create abort controller for disconnected component');
      return new AbortController(); // Return a basic controller
    }
    
    const controller = new AbortController();
    this._abortControllers.push(controller);
    
    this._log.blue('Created managed abort controller');
    return controller;
  }
  
  /**
   * Remove all event listeners
   */
  _removeAllEventListeners() {
    this._eventListeners.forEach(listener => {
      try {
        listener.cleanup();
      } catch (error) {
        this._log.red('Error removing event listener:', error);
      }
    });
    this._eventListeners = [];
    
    if (this._eventListeners.length > 0) {
      this._log.green(`Removed ${this._eventListeners.length} event listeners`);
    }
  }
  
  /**
   * Clear all intervals and timeouts
   */
  _clearAllTimers() {
    // Clear intervals
    this._intervals.forEach(intervalId => {
      clearInterval(intervalId);
    });
    if (this._intervals.length > 0) {
      this._log.green(`Cleared ${this._intervals.length} intervals`);
    }
    this._intervals = [];
    
    // Clear timeouts
    this._timeouts.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    if (this._timeouts.length > 0) {
      this._log.green(`Cleared ${this._timeouts.length} timeouts`);
    }
    this._timeouts = [];
  }
  
  /**
   * Abort all ongoing requests
   */
  _abortAllRequests() {
    this._abortControllers.forEach(controller => {
      if (!controller.signal.aborted) {
        controller.abort('Component disconnecting');
      }
    });
    if (this._abortControllers.length > 0) {
      this._log.green(`Aborted ${this._abortControllers.length} requests`);
    }
    this._abortControllers = [];
  }
  
  /**
   * Render error state when initialization fails
   */
  _renderErrorState() {
    this.innerHTML = `
      <div class="error-state">
        <h3>Component Error</h3>
        <p>The ${this._componentName} component failed to initialize properly.</p>
        <button type="button" onclick="window.location.reload()" aria-label="Reload page to fix error">
          Reload Page
        </button>
      </div>
    `;
  }
  
  /**
   * Get component lifecycle statistics for debugging
   */
  getLifecycleStats() {
    return {
      componentName: this._componentName,
      isConnected: this._isConnected,
      isInitialized: this._isInitialized,
      cleanupFunctions: this._cleanupFunctions.length,
      eventListeners: this._eventListeners.length,
      intervals: this._intervals.length,
      timeouts: this._timeouts.length,
      abortControllers: this._abortControllers.length
    };
  }
  
  /**
   * Log current lifecycle statistics
   */
  logLifecycleStats() {
    const stats = this.getLifecycleStats();
    this._log.table(stats);
  }
}
